import axios, { AxiosError } from 'axios';
import config from '@/config';

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401 && onUnauthorized) {
      try {
        onUnauthorized();
      } catch {
        // Ignore handler errors so the original rejection still surfaces.
      }
    }
    const detail = error.response?.data as { detail?: string | Array<{ msg?: string }> } | undefined;
    let message: string;
    if (typeof detail?.detail === 'string') {
      message = detail.detail;
    } else if (Array.isArray(detail?.detail) && detail.detail[0]?.msg) {
      message = detail.detail[0].msg;
    } else {
      message = error.message;
    }
    // Preserve the HTTP status on the Error so callers can branch on it
    // (e.g. treat 409 as "already running"). Additive — existing callers that
    // only read `.message` are unaffected.
    const wrapped = new Error(message) as Error & { status?: number };
    wrapped.status = error.response?.status;
    return Promise.reject(wrapped);
  }
);

export default api;
